// React
import React, { useMemo } from 'react';

// Mantine
import { Select, Tooltip, useComputedColorScheme } from '@mantine/core';
import { useTranslation } from '@refinedev/core';

// Custom Components
import { useActiveForm } from 'pages/claims/components';

// Utils
import { getValueByPath } from 'utils/getValueByPath';

interface ModalSelectOptions {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ModalSelectInputProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  accessorKey: string;
  options: ModalSelectOptions[];
  enableTooltip?: boolean;
  tooltipText?: string;
  dataTestId?: string;
  disabled?: boolean;
}

export const ModalSelectInput: React.FC<ModalSelectInputProps> = ({
  label,
  placeholder,
  required = false,
  accessorKey,
  options,
  enableTooltip = false,
  tooltipText = '',
  dataTestId,
  disabled = false,
}) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const { translate } = useTranslation();
  const { form, formType, prefix } = useActiveForm();
  const path = prefix ? `${prefix}.${accessorKey}` : accessorKey;
  const { error } = form.getInputProps(path);

  // Derive current value directly from form state; treat '' as unselected (null) for Mantine Select
  const rawValue = useMemo(
    () => getValueByPath(form.getValues(), path),
    [form, path]
  );
  const selectValue =
    rawValue === '' || rawValue === undefined ? null : rawValue;

  const handleChange = (newVal: string | null) => {
    const value =
      formType === 'bulkEdit' && accessorKey === 'payorID' && newVal === null
        ? undefined
        : newVal;
    form.setFieldValue(path, value);
  };

  return (
    <React.Fragment key={accessorKey}>
      <Tooltip
        label={enableTooltip ? tooltipText : ''}
        disabled={!enableTooltip}
        withArrow
        position="top"
      >
        {options.length === 1 ? (
          <Select
            key={path}
            radius={'md'}
            withAsterisk={required}
            size="md"
            label={label}
            data={options}
            allowDeselect={false}
            disabled
            c={color}
            value={options[0].value}
            error={error}
            data-testid={dataTestId}
          />
        ) : (
          <Select
            key={path}
            label={label}
            radius="md"
            withAsterisk={required}
            size="md"
            placeholder={
              placeholder || translate('placeholders.selectAnOption')
            }
            clearable
            searchable
            allowDeselect
            c={color}
            value={selectValue}
            onChange={handleChange}
            data={options}
            data-testid={dataTestId}
            error={error}
            disabled={disabled}
          />
        )}
      </Tooltip>
    </React.Fragment>
  );
};
